import { useCallback, useEffect, useRef, useState } from "react";
import { GERI_ALMA_DAKIKA, MESAJ_EN_COK } from "@koclukpanel/contracts";
import { BosDurum, Buton, Iskelet, Kart, MetinAlani, NotKutusu, Rozet, cn } from "@koclukpanel/ui";
import {
  ApiHatasi,
  mesajEkBaglantisi,
  mesajEkliGonder,
  mesajGeriAl,
  mesajGonder,
  mesajKisileri,
  mesajKutum,
  mesajOkundu,
  mesajYazisma,
  type MesajEki,
  type MesajKanali,
  type YazilabilirKisiler,
  type YazismaMesaji,
  type YazismaOzeti,
} from "./api";

/**
 * MESAJLAR — öğrenci ve veli tarafı (F-MSG)
 *
 * ================================================================
 * "KURUMA YAZ" GİZLENMİYOR
 * ================================================================
 * Bu kanalın var olma sebebi, koçuyla sorun yaşayan öğrencinin gidecek
 * bir yeri olması (F-MSG-01a). Bir alt menüye gömseydik, tam da en çok
 * gerektiği anda — öğrencinin çekindiği anda — bulunamazdı. O yüzden
 * kutunun üstünde, koça yazmakla aynı görünürlükte duruyor.
 *
 * ================================================================
 * "İSTEK VE YARDIM"DAN FARKI EKRANDA YAZILI
 * ================================================================
 * `TalepKutusu` bir kuyruk: kategorisi, durumu ve karar veren biri var.
 * Burası bir yazışma. İkisini karıştırmak, "uzmanımı değiştirin"i
 * sohbete, "iyi geceler"i onay kuyruğuna sokardı.
 *
 * ================================================================
 * BU EKRAN OKUNDU BİLGİSİ ÜRETİYOR
 * ================================================================
 * Yazışmayı açmak karşı tarafın geri alma hakkını bitiriyor
 * (F-MSG-01b). Damga yazışma AÇILDIĞINDA atılıyor, kutu çizilirken
 * değil — kutuyu görmek okumak değildir.
 */

/**
 * DİL KİPİ — öğrenciye "sen", veliye "siz".
 *
 * ================================================================
 * KURAL BU DEPODA YAZILI VE BİR KEZ UYGULANMADI
 * ================================================================
 * `VeliKabugu.tsx` notu: *"Veli öğrenci değil."* Aynı not, kuralın
 * 05.09.2026'da YAZILDIĞINI ama giriş akışında UYGULANMADIĞINI da
 * kaydediyor. Bu ekran ikisine de görünüyor; metni tek bir kipte
 * yazsaydık aynı hata üçüncü kez olurdu.
 *
 * Sözlük olarak duruyor ki bir metin eklerken ötekini yazmayı
 * unutmak DERLEME hatası versin.
 */
type Kip = "sen" | "siz";

interface KipMetni {
  mesajinEtiket: string;
  mesajinIpucu: string;
  geriAlmaNotu: string;
  kimeBaslik: string;
  kurumDugme: string;
  kurumNotu: string;
  kimseYok: string;
  bosBaslik: string;
  bosAciklama: string;
  kocEtiketi: string;
}

const METIN: Record<Kip, KipMetni> = {
  sen: {
    mesajinEtiket: "Mesajın",
    mesajinIpucu: "Mesajını yaz",
    geriAlmaNotu:
      "Gönderdikten sonra {dk} dakika içinde geri alabilirsin — karşı taraf okumadıysa.",
    kimeBaslik: "Kime yazmak istiyorsun?",
    kurumDugme: "Kuruma yaz — koçun dışında biriyle konuş",
    kurumNotu:
      "Bu mesajı koçun görmez; yönetimden biri yanıtlar. Koçunla ilgili bir sorunu da buraya yazabilirsin.",
    kimseYok: "Şu an yazışabileceğin kimse yok. Koç ataman yapıldığında koçun burada görünür.",
    bosBaslik: "Henüz mesajın yok",
    bosAciklama: "Koçuna ya da kuruma yazmak için “Yeni mesaj”a dokun.",
    kocEtiketi: "Koçum",
  },
  siz: {
    mesajinEtiket: "Mesajınız",
    mesajinIpucu: "Mesajınızı yazın",
    geriAlmaNotu:
      "Gönderdikten sonra {dk} dakika içinde geri alabilirsiniz — karşı taraf okumadıysa.",
    kimeBaslik: "Kime yazmak istiyorsunuz?",
    kurumDugme: "Kuruma yazın — çocuğunuzun koçu dışında biriyle görüşün",
    kurumNotu:
      "Bu mesajı çocuğunuzun koçu görmez; yönetimden biri yanıtlar. Koçla ilgili bir sorunu da buraya yazabilirsiniz.",
    kimseYok: "Şu an yazışabileceğiniz kimse yok. Çocuğunuza koç atandığında koç burada görünür.",
    bosBaslik: "Henüz mesajınız yok",
    bosAciklama: "Koça ya da kuruma yazmak için “Yeni mesaj”a dokunun.",
    kocEtiketi: "Koç",
  },
};

function kanalAdi(kanal: MesajKanali, kip: Kip): string {
  if (kanal === "KOC") return METIN[kip].kocEtiketi;
  return kanal === "KURUM" ? "Kurum" : "Personel";
}

/** Okunabilir boyut. Öğrenci mobil veriyle indirecek — kaç MB olduğu görünsün. */
export function ekBoyutu(bayt: number): string {
  if (bayt >= 1024 * 1024) return `${(bayt / (1024 * 1024)).toFixed(1)} MB`;
  if (bayt >= 1024) return `${Math.round(bayt / 1024)} KB`;
  return `${bayt} B`;
}

/**
 * Mesaj eki — F-MSG-03.
 *
 * Bağlantı TIKLAYINCA üretiliyor: imzalı bağlantı 15 dakika yaşıyor ve
 * yazışma açılırken hepsini üretseydik, ekranı açık bırakan kullanıcının
 * bağlantıları sessizce ölürdü. Ayrıca her indirme denetime geçiyor —
 * açmadığı eki indirmiş saymak yanlış olurdu.
 */
function Ek({ messageId, ek }: { messageId: string; ek: MesajEki }) {
  const [aciliyor, setAciliyor] = useState(false);
  const [hata, setHata] = useState<string | null>(null);

  const ac = async () => {
    setAciliyor(true);
    setHata(null);
    try {
      const s = await mesajEkBaglantisi(messageId);
      window.open(s.url, "_blank", "noopener");
    } catch (e) {
      setHata(e instanceof ApiHatasi ? e.message : "Ek açılamadı.");
    } finally {
      setAciliyor(false);
    }
  };

  return (
    <div className="mt-1">
      <Buton
        gorunum="ikincil"
        onClick={() => void ac()}
        yukleniyor={aciliyor}
        yukleniyorMetni="Açılıyor"
      >
        📎 {ek.ad} · {ekBoyutu(ek.boyut)}
      </Buton>
      {hata && (
        <p role="alert" className="text-hata-700 mt-1 text-sm">
          {hata}
        </p>
      )}
    </div>
  );
}

function zaman(iso: string): string {
  return new Date(iso).toLocaleString("tr-TR", {
    day: "numeric",
    month: "long",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * "Geri al" düğmesi görünsün mü — EKRANIN TAHMİNİ.
 *
 * Son sözü sunucu söylüyor: okunmuş bir mesaj süre dolmamış olsa bile
 * geri alınamıyor ve ekran karşı tarafın okuma damgasını görmüyor.
 * Süreye hiç bakmadan düğmeyi hep göstermek de olurdu ama iki gün
 * önceki mesajın altındaki "Geri al", olmayan bir hak vaat ederdi.
 */
export function geriAlmaGorunurMu(mesaj: YazismaMesaji, simdi: Date): boolean {
  if (!mesaj.benimMi || mesaj.geriAlindiMi) return false;
  return (simdi.getTime() - new Date(mesaj.createdAt).getTime()) / 60_000 <= GERI_ALMA_DAKIKA;
}

function Balon({
  mesaj,
  onGeriAl,
  geriAliniyor,
}: {
  mesaj: YazismaMesaji;
  onGeriAl: () => void;
  geriAliniyor: boolean;
}) {
  const [simdi, setSimdi] = useState(() => new Date());

  /*
    Pencere SAYAÇLA kapanıyor. Sayaç olmasaydı düğme süre dolduktan
    sonra da ekranda dururdu; basan kişi hata alırdı ve hata doğru
    olurdu ama arayüz yalan söylemiş olurdu.
  */
  useEffect(() => {
    if (!mesaj.benimMi || mesaj.geriAlindiMi) return;
    const t = setInterval(() => setSimdi(new Date()), 5_000);
    return () => clearInterval(t);
  }, [mesaj.benimMi, mesaj.geriAlindiMi]);

  return (
    <li className={cn("flex flex-col gap-1", mesaj.benimMi ? "items-end" : "items-start")}>
      <div
        className={cn(
          "rounded-alan max-w-[85%] px-3 py-2",
          mesaj.benimMi ? "bg-marka-600 text-notr-0" : "bg-notr-100 text-notr-900",
        )}
      >
        {!mesaj.benimMi && <p className="text-notr-600 text-xs font-medium">{mesaj.gonderenAd}</p>}
        {mesaj.geriAlindiMi ? (
          /*
            "SİLİNDİ" DEMİYORUZ — silinmedi. Gövde veritabanında duruyor
            ve gerekçeli bir denetim incelemesinde görünüyor. Yanlış bir
            güvence vermek, geri almayı kanıt yok etmenin yolu sanmaya
            yol açardı.
          */
          <p className="text-notr-600 text-sm italic">Bu mesaj geri alındı.</p>
        ) : (
          <p className="text-sm break-words whitespace-pre-wrap">{mesaj.govde}</p>
        )}
        {mesaj.ek && <Ek messageId={mesaj.id} ek={mesaj.ek} />}
      </div>
      <span className="text-notr-600 text-xs">{zaman(mesaj.createdAt)}</span>
      {geriAlmaGorunurMu(mesaj, simdi) && (
        <Buton
          gorunum="sessiz"
          onClick={onGeriAl}
          yukleniyor={geriAliniyor}
          yukleniyorMetni="Geri alınıyor"
        >
          Geri al
        </Buton>
      )}
    </li>
  );
}

function Yazisma({
  id,
  kip,
  onGeri,
  onOkundu,
}: {
  id: string;
  kip: Kip;
  onGeri: () => void;
  onOkundu?: () => void;
}) {
  const [mesajlar, setMesajlar] = useState<YazismaMesaji[]>([]);
  const [kanal, setKanal] = useState<MesajKanali>("KOC");
  const [arsivlendiMi, setArsivlendiMi] = useState(false);
  const [mesai, setMesai] = useState<{ icindeMi: boolean; metin: string } | null>(null);
  const [yukleniyor, setYukleniyor] = useState(true);
  const [hata, setHata] = useState<string | null>(null);
  const [taslak, setTaslak] = useState("");
  const [dosya, setDosya] = useState<File | null>(null);
  const dosyaAlani = useRef<HTMLInputElement | null>(null);
  const [gonderiliyor, setGonderiliyor] = useState(false);
  const [geriAlinan, setGeriAlinan] = useState<string | null>(null);
  const liste = useRef<HTMLUListElement | null>(null);

  const yukle = useCallback(
    async (damgaAt: boolean) => {
      setHata(null);
      try {
        const y = await mesajYazisma(id);
        setMesajlar(y.mesajlar);
        setKanal(y.kanal);
        setArsivlendiMi(y.arsivlendiMi);
        setMesai(y.mesai);
        /*
          DAMGA YALNIZCA İLK AÇILIŞTA. Her tazelemede atmak, gönderme
          sonrası da "okudum" demek olurdu — oysa kendi mesajını
          göndermek karşı tarafınkini okumak değildir.
        */
        if (damgaAt) {
          await mesajOkundu(id);
          // Kabuktaki rozet HEMEN düşsün; sayaç 60 sn'de bir dönüyor.
          onOkundu?.();
        }
      } catch (e) {
        setHata(e instanceof ApiHatasi ? e.message : "Yazışma açılamadı.");
      } finally {
        setYukleniyor(false);
      }
    },
    [id, onOkundu],
  );

  useEffect(() => {
    void yukle(true);
  }, [yukle]);

  /*
    LİSTENİN KENDİSİ KAYDIRILIYOR, `scrollIntoView` DEĞİL: ikincisi
    kapsayan her kaydırılabilir kutuyu oynatır ve telefonda bütün
    sayfayı zıplatır.
  */
  useEffect(() => {
    const el = liste.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [mesajlar.length]);

  const gonder = async () => {
    setGonderiliyor(true);
    setHata(null);
    try {
      if (dosya) {
        await mesajEkliGonder({ kanal, conversationId: id, govde: taslak, dosya });
      } else {
        await mesajGonder({ kanal, conversationId: id, govde: taslak });
      }
      setTaslak("");
      setDosya(null);
      /*
        `input type=file` değeri React durumundan AYRI yaşıyor: durumu
        temizlemek alanı temizlemiyor ve aynı dosya ikinci kez
        seçilemiyordu.
      */
      if (dosyaAlani.current) dosyaAlani.current.value = "";
      await yukle(false);
    } catch (e) {
      setHata(e instanceof ApiHatasi ? e.message : "Mesaj gönderilemedi.");
    } finally {
      setGonderiliyor(false);
    }
  };

  const geriAl = async (messageId: string) => {
    setGeriAlinan(messageId);
    setHata(null);
    try {
      await mesajGeriAl(messageId);
      await yukle(false);
    } catch (e) {
      /*
        SUNUCUNUN SEBEBİ AYNEN GÖSTERİLİYOR. "Okundu" ile "süre geçti"
        iki farklı şey söylüyor; tek bir "geri alınamadı" ikisini de
        gizlerdi.
      */
      setHata(e instanceof ApiHatasi ? e.message : "Mesaj geri alınamadı.");
    } finally {
      setGeriAlinan(null);
    }
  };

  return (
    <div className="flex flex-1 flex-col">
      <div className="flex items-center gap-2 px-4 pt-4">
        <Buton gorunum="sessiz" onClick={onGeri}>
          ← Mesajlar
        </Buton>
        <Rozet ton="notr">{kanalAdi(kanal, kip)}</Rozet>
      </div>

      {yukleniyor ? (
        <div className="p-4">
          <Iskelet satir={4} />
        </div>
      ) : (
        <ul ref={liste} className="flex flex-1 flex-col gap-3 overflow-y-auto p-4">
          {mesajlar.length === 0 ? (
            <li className="text-notr-600 text-sm">Bu yazışmada henüz mesaj yok.</li>
          ) : (
            mesajlar.map((m) => (
              <Balon
                key={m.id}
                mesaj={m}
                onGeriAl={() => void geriAl(m.id)}
                geriAliniyor={geriAlinan === m.id}
              />
            ))
          )}
        </ul>
      )}

      {arsivlendiMi ? (
        /*
          ARŞİVLENMİŞ YAZIŞMADA YAZMA KUTUSU HİÇ ÇİZİLMİYOR — F-MSG-07.
          Kutuyu gösterip gönderince hata vermek, kullanıcıya yazdırıp
          sonra "olmadı" demek olur.
        */
        <div className="border-notr-200 border-t p-4">
          <p role="status" className="text-notr-700 text-sm">
            Bu yazışma arşivlendi; yeni mesaj gönderilemez. Geçmişi okuyabilirsin.
          </p>
        </div>
      ) : (
        <div className="border-notr-200 flex flex-col gap-2 border-t p-4">
          {mesai && !mesai.icindeMi && (
            /*
              MESAİ DIŞI NOTU — F-MSG-05. Mesaj yine gönderiliyor:
              engellenseydi gece geç saatte bir şey anlatmak isteyen
              öğrenci onu hiç yazamaz ve sabaha unuturdu. Engellenen tek
              şey yanlış beklenti.

              Mesai İÇİNDEYKEN not gösterilmiyor — "şu an mesaide"
              demek, cevabın hemen geleceği sözünü verirdi.
            */
            <NotKutusu ton="bilgi">
              Şu an mesai saati dışında. Mesajın iletilir; yanıt {mesai.metin} arasında gelir.
            </NotKutusu>
          )}
          <MetinAlani
            etiket={METIN[kip].mesajinEtiket}
            etiketGizli
            rows={3}
            maxLength={MESAJ_EN_COK}
            placeholder={METIN[kip].mesajinIpucu}
            value={taslak}
            onChange={(e) => setTaslak(e.target.value)}
          />
          <input
            ref={dosyaAlani}
            type="file"
            aria-label="Dosya ekle"
            /*
              `accept` yalnızca bir İPUCU ve telefonun dosya seçicisini
              daraltıyor; gerçek kontrol sunucuda, İÇERİKTEN yapılıyor.
            */
            accept="image/jpeg,image/png,image/webp,application/pdf"
            onChange={(e) => setDosya(e.target.files?.[0] ?? null)}
            className="text-notr-700 text-sm"
          />
          <p className="text-notr-600 text-xs">
            {METIN[kip].geriAlmaNotu.replace("{dk}", String(GERI_ALMA_DAKIKA))} Ek: görsel veya PDF,
            en çok 8 MB.
          </p>
          <Buton
            onClick={() => void gonder()}
            yukleniyor={gonderiliyor}
            yukleniyorMetni="Gönderiliyor"
            // Ek varsa gövde boş olabilir — sunucu da aynı kuralı uyguluyor.
            disabled={taslak.trim().length === 0 && dosya === null}
          >
            Gönder
          </Buton>
          {hata && (
            <p role="alert" className="text-hata-700 text-sm">
              {hata}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function YeniMesaj({
  kisiler,
  kip,
  onAcildi,
  onVazgec,
}: {
  kisiler: YazilabilirKisiler;
  kip: Kip;
  onAcildi: (conversationId: string) => void;
  onVazgec: () => void;
}) {
  const [hedef, setHedef] = useState<string | null>(null);
  const [govde, setGovde] = useState("");
  const [gonderiliyor, setGonderiliyor] = useState(false);
  const [hata, setHata] = useState<string | null>(null);

  const gonder = async () => {
    if (!hedef) return;
    setGonderiliyor(true);
    setHata(null);
    try {
      const ayrac = hedef.indexOf(":");
      const kanal = (ayrac === -1 ? hedef : hedef.slice(0, ayrac)) as MesajKanali;
      const sonuc = await mesajGonder({
        kanal,
        aliciId: ayrac === -1 ? null : hedef.slice(ayrac + 1),
        govde,
      });
      onAcildi(sonuc.conversationId);
    } catch (e) {
      setHata(e instanceof ApiHatasi ? e.message : "Mesaj gönderilemedi.");
    } finally {
      setGonderiliyor(false);
    }
  };

  if (hedef === null) {
    return (
      <Kart baslik={METIN[kip].kimeBaslik}>
        <div className="flex flex-col gap-2">
          {kisiler.koc.map((k) => (
            <Buton key={k.userId} gorunum="ikincil" onClick={() => setHedef(`KOC:${k.userId}`)}>
              {k.ad} — {k.iliski}
            </Buton>
          ))}
          {kisiler.kurumAcabilirMi && (
            /*
              KURUM SEÇENEĞİ AYNI YERDE VE GÖRÜNÜR. Bu kanalın var olma
              sebebi koçuyla sorun yaşayan öğrencinin gidecek yeri
              olması; bir alt menüye gömseydik tam da gerektiği anda
              bulunamazdı.
            */
            <Buton gorunum="ikincil" onClick={() => setHedef("KURUM")}>
              {METIN[kip].kurumDugme}
            </Buton>
          )}
          {kisiler.koc.length === 0 && !kisiler.kurumAcabilirMi && (
            <p className="text-notr-700 text-sm">{METIN[kip].kimseYok}</p>
          )}
          <Buton gorunum="sessiz" onClick={onVazgec}>
            Vazgeç
          </Buton>
        </div>
      </Kart>
    );
  }

  return (
    <Kart baslik={hedef === "KURUM" ? "Kuruma mesaj" : "Yeni mesaj"}>
      <div className="flex flex-col gap-3">
        {hedef === "KURUM" && <p className="text-notr-700 text-sm">{METIN[kip].kurumNotu}</p>}
        <MetinAlani
          etiket={METIN[kip].mesajinEtiket}
          rows={4}
          maxLength={MESAJ_EN_COK}
          value={govde}
          onChange={(e) => setGovde(e.target.value)}
        />
        <div className="flex flex-wrap gap-2">
          <Buton
            onClick={() => void gonder()}
            yukleniyor={gonderiliyor}
            yukleniyorMetni="Gönderiliyor"
            disabled={govde.trim().length === 0}
          >
            Gönder
          </Buton>
          <Buton gorunum="sessiz" onClick={() => setHedef(null)}>
            Geri
          </Buton>
        </div>
        {hata && (
          <p role="alert" className="text-hata-700 text-sm">
            {hata}
          </p>
        )}
      </div>
    </Kart>
  );
}

export function Mesajlar({
  kip = "sen",
  onGeri,
  onOkundu,
}: {
  kip?: Kip;
  onGeri?: () => void;
  /** Bir yazışma okunduğunda çağrılıyor — kabuğun rozeti için. */
  onOkundu?: () => void;
} = {}) {
  const [yazismalar, setYazismalar] = useState<YazismaOzeti[]>([]);
  const [kisiler, setKisiler] = useState<YazilabilirKisiler | null>(null);
  const [yukleniyor, setYukleniyor] = useState(true);
  const [hata, setHata] = useState<string | null>(null);
  const [acik, setAcik] = useState<string | null>(null);
  const [yeni, setYeni] = useState(false);

  const yukle = useCallback(async () => {
    setYukleniyor(true);
    setHata(null);
    try {
      const [kutu, k] = await Promise.all([mesajKutum(), mesajKisileri()]);
      setYazismalar(kutu.yazismalar);
      setKisiler(k);
    } catch (e) {
      setHata(e instanceof ApiHatasi ? e.message : "Mesajlar alınamadı.");
    } finally {
      setYukleniyor(false);
    }
  }, []);

  useEffect(() => {
    void yukle();
  }, [yukle]);

  if (acik !== null) {
    return (
      <Yazisma
        id={acik}
        kip={kip}
        onOkundu={onOkundu}
        onGeri={() => {
          setAcik(null);
          void yukle();
        }}
      />
    );
  }

  return (
    <div className="flex flex-col gap-4 p-4">
      <div className="flex items-center gap-2">
        {/*
          GERİ DÜĞMESİ YALNIZCA ÇAĞIRAN İSTERSE. Öğrencide bu ekran
          `Hesabım`ın alt ekranı ve geri düğmesi ORADA duruyor; iki
          tane çizmek, ikisinin farklı yerlere götürdüğünü düşündürürdü.
          Veli portalında ekranı devralıyor, o yüzden kendi geri
          düğmesi gerekiyor.
        */}
        {onGeri && (
          <Buton gorunum="sessiz" onClick={onGeri}>
            ← Geri
          </Buton>
        )}
        <h1 className="text-notr-900 text-lg font-semibold">Mesajlar</h1>
      </div>

      {yukleniyor ? (
        <Iskelet satir={4} />
      ) : hata ? (
        <BosDurum
          ton="hata"
          baslik="Mesajlar alınamadı"
          aciklama={hata}
          eylem={
            <Buton gorunum="ikincil" onClick={() => void yukle()}>
              Tekrar dene
            </Buton>
          }
        />
      ) : yeni && kisiler ? (
        <YeniMesaj
          kisiler={kisiler}
          kip={kip}
          onVazgec={() => setYeni(false)}
          onAcildi={(id) => {
            setYeni(false);
            setAcik(id);
          }}
        />
      ) : (
        <>
          <Buton onClick={() => setYeni(true)}>Yeni mesaj</Buton>

          {yazismalar.length === 0 ? (
            <BosDurum baslik={METIN[kip].bosBaslik} aciklama={METIN[kip].bosAciklama} />
          ) : (
            <ul className="flex flex-col gap-2">
              {yazismalar.map((y) => (
                <li key={y.id}>
                  <button
                    type="button"
                    onClick={() => setAcik(y.id)}
                    className={cn(
                      "border-notr-200 bg-notr-0 rounded-alan min-h-dokunma w-full border p-3 text-left",
                      "focus-visible:outline-marka-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2",
                    )}
                  >
                    <div className="flex items-baseline justify-between gap-2">
                      <span
                        className={cn(
                          "text-notr-900 min-w-0 truncate text-sm",
                          y.okunmamis > 0 && "font-semibold",
                        )}
                      >
                        {y.karsiTaraf}
                      </span>
                      {y.okunmamis > 0 && <Rozet ton="bilgi">{y.okunmamis}</Rozet>}
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-2">
                      <Rozet ton="notr">{kanalAdi(y.kanal, kip)}</Rozet>
                      {y.arsivlendiMi && <Rozet ton="notr">Arşiv</Rozet>}
                      <span className="text-notr-600 text-xs">{zaman(y.sonMesajAt)}</span>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
}
